/**
 * RisicoInboxPage — office-inbox voor risico-meldingen, gegroepeerd per
 * Werk_Order.
 *
 * v2 (VITE_RISICO_V2_ENABLED aan) heeft drie secties, die samen het proces zijn:
 *  1. Te beoordelen (Gemeld)    — de junior meldde iets; de senior bepaalt of
 *     het echt een risico is of dat het kleed vrijgegeven kan worden.
 *  2. Te versturen (Bevestigd)  — bevestigde risico's; één mail per werkorder,
 *     geschreven door de AI, met extra context van de senior.
 *  3. Wacht op klant (Wacht_klant) — mail is de deur uit; het antwoord komt in
 *     de inbox en wordt hier verwerkt met vier knoppen.
 *
 * Staat de flag uit, dan draait de oude twee-secties-UI (Gemeld + Wacht op
 * klant met "Beslis namens klant"). Dat is de rollback van de flip.
 *
 * Polling elke 60s voor alle secties.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Card, CardContent, Button, Spinner, Badge } from '../components/ui';
import { RisicoBundleCard } from '../components/risico/RisicoBundleCard';
import { RisicoBeoordeelCard } from '../components/risico/RisicoBeoordeelCard';
import { RisicoComposerCard } from '../components/risico/RisicoComposerCard';
import { VerwerkAntwoordDialog } from '../components/risico/VerwerkAntwoordDialog';
import { BeslisNamensKlantDialog } from '../components/risico/BeslisNamensKlantDialog';
import {
  fetchRisicoBundles,
  NAMENS_KLANT_UI_ENABLED,
  RISICO_V2_ENABLED,
  type RisicoBundle,
  type RisicoBundleItem,
} from '../lib/risico';

const POLL_MS = 60_000;
const ESCALATIE_DAGEN = 7;

function wachtDagen(item: RisicoBundleItem): number | null {
  const anker = item.mailVerstuurdOp ?? item.gemeldOp;
  if (!anker) return null;
  const ms = Date.now() - new Date(anker).getTime();
  if (ms <= 0) return 0;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function tokenStatus(item: RisicoBundleItem): 'ok' | 'bijna' | 'verlopen' | null {
  if (!item.tokenExpiry) return null;
  const rest = new Date(item.tokenExpiry).getTime() - Date.now();
  if (rest < 0) return 'verlopen';
  if (rest < 3 * 24 * 60 * 60 * 1000) return 'bijna';
  return 'ok';
}

/** Verwijder één kleed uit een bundle-lijst; lege bundles vallen weg. */
function zonderItem(bundles: RisicoBundle[], wasserijItemId: string): RisicoBundle[] {
  return bundles
    .map((b) => ({ ...b, items: b.items.filter((it) => it.wasserijItemId !== wasserijItemId) }))
    .filter((b) => b.items.length > 0);
}

export function RisicoInboxPage() {
  return RISICO_V2_ENABLED ? <RisicoInboxV2 /> : <RisicoInboxV1 />;
}

// ─── v2: drie secties ───────────────────────────────────────────────────────

function RisicoInboxV2() {
  const [teBeoordelen, setTeBeoordelen] = useState<RisicoBundle[]>([]);
  const [teVersturen, setTeVersturen] = useState<RisicoBundle[]>([]);
  const [wacht, setWacht] = useState<RisicoBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dialogItem, setDialogItem] = useState<{
    item: RisicoBundleItem;
    klantNaam: string | null;
  } | null>(null);

  const load = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    const [gemeld, bevestigd, wachtend] = await Promise.allSettled([
      fetchRisicoBundles('gemeld'),
      fetchRisicoBundles('bevestigd'),
      fetchRisicoBundles('wacht_klant'),
    ]);
    const volgende: Record<string, string> = {};
    if (gemeld.status === 'fulfilled') setTeBeoordelen(gemeld.value);
    else volgende.gemeld = foutTekst(gemeld.reason, 'Kon te beoordelen meldingen niet laden');
    if (bevestigd.status === 'fulfilled') setTeVersturen(bevestigd.value);
    else volgende.bevestigd = foutTekst(bevestigd.reason, 'Kon te versturen bundles niet laden');
    if (wachtend.status === 'fulfilled') setWacht(wachtend.value);
    else volgende.wacht = foutTekst(wachtend.reason, 'Kon wacht-lijst niet laden');
    setErrors(volgende);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Beoordeeld: het kleed verlaat deze sectie en duikt (bij "bevestig") op in
  // "Te versturen". Optimistisch weghalen, daarna de echte staat ophalen.
  const handleBeoordeeld = (wasserijItemId: string) => {
    setTeBeoordelen((prev) => zonderItem(prev, wasserijItemId));
    void load(true);
  };

  const handleVerstuurd = (workOrderId: string) => {
    setTeVersturen((prev) => prev.filter((b) => b.workOrderId !== workOrderId));
    void load(true);
  };

  const handleVerwerkt = (wasserijItemId: string) => {
    setDialogItem(null);
    setWacht((prev) => zonderItem(prev, wasserijItemId));
    void load(true);
  };

  const aantalBeoordelen = teBeoordelen.reduce((a, b) => a + b.items.length, 0);
  const aantalVersturen = teVersturen.reduce((a, b) => a + b.items.length, 0);
  const aantalWacht = wacht.reduce((a, b) => a + b.items.length, 0);

  return (
    <>
      <div className="flex-1 overflow-y-auto bg-slate-50">
        <div className="mx-auto max-w-4xl space-y-8 px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Risico's</h1>
              <p className="mt-1 text-sm text-gray-600">
                {aantalBeoordelen} te beoordelen · {aantalVersturen} te versturen · {aantalWacht}{' '}
                wacht op klant
              </p>
            </div>
            <Button variant="secondary" onClick={() => void load(true)} disabled={refreshing || loading}>
              {refreshing ? 'Vernieuwen…' : 'Vernieuwen'}
            </Button>
          </div>

          {loading && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Spinner />
              </CardContent>
            </Card>
          )}

          {!loading && (
            <>
              <Sectie
                titel="Te beoordelen"
                aantal={aantalBeoordelen}
                error={errors.gemeld}
                leeg="Niets te beoordelen. Meldingen uit de field-app verschijnen hier."
                heeftInhoud={teBeoordelen.length > 0}
              >
                {teBeoordelen.map((bundle) => (
                  <RisicoBeoordeelCard
                    key={bundle.workOrderId}
                    bundle={bundle}
                    onBeoordeeld={handleBeoordeeld}
                  />
                ))}
              </Sectie>

              <Sectie
                titel="Te versturen"
                aantal={aantalVersturen}
                error={errors.bevestigd}
                leeg="Geen bevestigde risico's die op een mail wachten."
                heeftInhoud={teVersturen.length > 0}
              >
                {teVersturen.map((bundle) => (
                  <RisicoComposerCard
                    key={bundle.workOrderId}
                    bundle={bundle}
                    onSent={() => handleVerstuurd(bundle.workOrderId)}
                  />
                ))}
              </Sectie>

              <Sectie
                titel="Wacht op klant"
                aantal={aantalWacht}
                error={errors.wacht}
                leeg="Geen openstaande antwoorden."
                heeftInhoud={wacht.length > 0}
              >
                {wacht.map((bundle) => (
                  <Card key={bundle.workOrderId}>
                    <CardContent className="space-y-3 py-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-gray-900">
                          {bundle.klantNaam || 'Onbekende klant'}
                          {bundle.workOrderNummer ? ` · ${bundle.workOrderNummer}` : ''}
                        </p>
                        <Badge variant="default">{bundle.items.length}×</Badge>
                      </div>
                      {bundle.items.map((item) => {
                        const dagen = wachtDagen(item);
                        return (
                          <div
                            key={item.wasserijItemId}
                            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3"
                          >
                            <div className="min-w-0">
                              <p className="text-sm text-gray-900">
                                {item.kleedNaam}{' '}
                                <span className="text-gray-500">({item.wiNummer})</span>
                              </p>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                                {dagen != null && (
                                  <span
                                    className={`rounded px-1.5 py-0.5 font-medium ${
                                      dagen >= ESCALATIE_DAGEN
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-gray-200 text-gray-700'
                                    }`}
                                  >
                                    wacht {dagen}d
                                  </span>
                                )}
                                {item.mailBounce && (
                                  <span
                                    className="rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-800"
                                    title="De risicomail kwam terug: de klant heeft hem niet. Corrigeer het adres op de klantkaart en stuur de mail opnieuw vanuit de thread."
                                  >
                                    mail kwam terug{item.mailBounce.adres ? ` (${item.mailBounce.adres})` : ''}
                                  </span>
                                )}
                                {item.conversationId && (
                                  <a
                                    href={`/inbox?conversation=${item.conversationId}`}
                                    className="text-indigo-600 hover:underline"
                                  >
                                    Open mailthread
                                  </a>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setDialogItem({ item, klantNaam: bundle.klantNaam })}
                              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                            >
                              Verwerk antwoord
                            </button>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                ))}
              </Sectie>
            </>
          )}
        </div>
      </div>

      {dialogItem && (
        <VerwerkAntwoordDialog
          wasserijItemId={dialogItem.item.wasserijItemId}
          kleedNaam={dialogItem.item.kleedNaam}
          klantNaam={dialogItem.klantNaam}
          conversationId={dialogItem.item.conversationId}
          onSuccess={handleVerwerkt}
          onClose={() => setDialogItem(null)}
        />
      )}
    </>
  );
}

function foutTekst(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}

function Sectie({
  titel,
  aantal,
  error,
  leeg,
  heeftInhoud,
  children,
}: {
  titel: string;
  aantal: number;
  error?: string;
  leeg: string;
  heeftInhoud: boolean;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">
        {titel} ({aantal})
      </h2>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {!error && !heeftInhoud && (
        <Card>
          <CardContent className="py-6 text-center text-sm text-gray-600">{leeg}</CardContent>
        </Card>
      )}
      {children}
    </section>
  );
}

// ─── v1: de oude twee secties (rollback-pad, vervalt bij de opruiming) ──────

function RisicoInboxV1() {
  const [bundles, setBundles] = useState<RisicoBundle[]>([]);
  const [wachtBundles, setWachtBundles] = useState<RisicoBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wachtError, setWachtError] = useState<string | null>(null);
  const [dialogItem, setDialogItem] = useState<{
    item: RisicoBundleItem;
    klantNaam: string | null;
  } | null>(null);

  const load = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    setError(null);
    setWachtError(null);
    const [gemeld, wacht] = await Promise.allSettled([
      fetchRisicoBundles('gemeld'),
      fetchRisicoBundles('wacht_klant'),
    ]);
    if (gemeld.status === 'fulfilled') setBundles(gemeld.value);
    else setError(foutTekst(gemeld.reason, 'Kon bundles niet laden'));
    if (wacht.status === 'fulfilled') setWachtBundles(wacht.value);
    else setWachtError(foutTekst(wacht.reason, 'Kon wacht-lijst niet laden'));
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(true), POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const removeBundle = (workOrderId: string) => {
    setBundles((prev) => prev.filter((b) => b.workOrderId !== workOrderId));
  };

  const removeWachtItem = (wasserijItemId: string) => {
    setDialogItem(null);
    setWachtBundles((prev) => zonderItem(prev, wasserijItemId));
    // Optimistic verwijderd; refetch pakt de echte staat mee.
    void load(true);
  };

  const totalKleden = bundles.reduce((acc, b) => acc + b.items.length, 0);
  const totalWacht = wachtBundles.reduce((acc, b) => acc + b.items.length, 0);

  return (
    <>
      <div className="flex-1 overflow-y-auto bg-slate-50">
        <div className="mx-auto max-w-4xl space-y-6 px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Risico-inbox</h1>
              <p className="mt-1 text-sm text-gray-600">
                {bundles.length === 0
                  ? 'Geen openstaande risico-meldingen'
                  : `${bundles.length} ${
                      bundles.length === 1 ? 'klant' : 'klanten'
                    } · ${totalKleden} ${totalKleden === 1 ? 'kleed' : 'kleden'} wacht op versturen`}
                {totalWacht > 0 && ` · ${totalWacht} wacht op klant`}
              </p>
            </div>
            <Button variant="secondary" onClick={() => void load(true)} disabled={refreshing || loading}>
              {refreshing ? 'Vernieuwen…' : 'Vernieuwen'}
            </Button>
          </div>

          {loading && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <Spinner />
              </CardContent>
            </Card>
          )}

          {!loading && error && (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="mb-3 text-red-600">{error}</p>
                <Button variant="secondary" onClick={() => void load()}>
                  Opnieuw proberen
                </Button>
              </CardContent>
            </Card>
          )}

          {!loading && !error && bundles.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="font-medium text-gray-900">Niks om te doen</p>
                <p className="mt-1 text-sm text-gray-600">
                  Field-medewerkers melden risico's tijdens de voorinspectie. Ze verschijnen hier
                  zodra ze binnenkomen.
                </p>
              </CardContent>
            </Card>
          )}

          {!loading &&
            !error &&
            bundles.map((bundle) => (
              <RisicoBundleCard
                key={bundle.workOrderId}
                bundle={bundle}
                onSent={() => removeBundle(bundle.workOrderId)}
              />
            ))}

          {!loading && (wachtBundles.length > 0 || wachtError) && (
            <div className="space-y-3 pt-2">
              <h2 className="text-lg font-semibold text-gray-900">Wacht op klant ({totalWacht})</h2>
              {wachtError && <p className="text-sm text-red-600">{wachtError}</p>}
              {wachtBundles.map((bundle) => (
                <Card key={bundle.workOrderId}>
                  <CardContent className="space-y-3 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900">
                        {bundle.klantNaam || 'Onbekende klant'}
                        {bundle.workOrderNummer ? ` · ${bundle.workOrderNummer}` : ''}
                      </p>
                      <Badge variant="default">{bundle.items.length}×</Badge>
                    </div>
                    {bundle.items.map((item) => {
                      const dagen = wachtDagen(item);
                      const tok = tokenStatus(item);
                      return (
                        <div
                          key={item.wasserijItemId}
                          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3"
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900">
                              {item.kleedNaam}{' '}
                              <span className="text-gray-500">({item.wiNummer})</span>
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                              {dagen != null && (
                                <span
                                  className={`rounded px-1.5 py-0.5 font-medium ${
                                    dagen >= ESCALATIE_DAGEN
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-gray-200 text-gray-700'
                                  }`}
                                >
                                  wacht {dagen}d
                                </span>
                              )}
                              {item.mailBounce && (
                                <span
                                  className="rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-800"
                                  title="De risicomail kwam terug: de klant heeft hem niet. Corrigeer het adres op de klantkaart en stuur de mail opnieuw vanuit de thread."
                                >
                                  mail kwam terug{item.mailBounce.adres ? ` (${item.mailBounce.adres})` : ''}
                                </span>
                              )}
                              {tok === 'verlopen' && (
                                <span className="rounded bg-red-100 px-1.5 py-0.5 font-medium text-red-800">
                                  link verlopen
                                </span>
                              )}
                              {tok === 'bijna' && (
                                <span className="rounded bg-red-50 px-1.5 py-0.5 font-medium text-red-700">
                                  link verloopt binnen 3 dagen
                                </span>
                              )}
                            </div>
                          </div>
                          {NAMENS_KLANT_UI_ENABLED && (
                            <button
                              type="button"
                              onClick={() => setDialogItem({ item, klantNaam: bundle.klantNaam })}
                              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
                            >
                              Beslis namens klant
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {dialogItem && (
        <BeslisNamensKlantDialog
          wasserijItemId={dialogItem.item.wasserijItemId}
          kleedNaam={dialogItem.item.kleedNaam}
          klantNaam={dialogItem.klantNaam}
          onSuccess={removeWachtItem}
          onClose={() => setDialogItem(null)}
        />
      )}
    </>
  );
}
